<template>
	<div>
		<b-modal :id="modalId" size="xl" @shown="onShown" @hide="onHide" hide-footer>
			<template #modal-title>
				{{ $t('winelist.addWineModal.title') }}
			</template>

			<template #default>
				<div>
					<StepNavigation :step="step" :stepTitles="stepTitles"/>
					<div @click="clickOutSide()" class="modal-inner-content">
						<Step1 v-if="step === 0" @cancel="hideModal" @okFromStep1="handleOkFromStep1" />
					<Step2 ref="step2" v-if="step === 1" @backFromStep2="handleBackFromStep2" @okFromStep2="handleOkFromStep2" :selectedWinery="selectedWinery" :selectedWines="selectedWines" />
					<Step3 ref="step3" v-if="step === 2" @backFromStep3="handleBackFromStep3" @okFromStep3="handleOkFromStep3" :selectedWinery="selectedWinery" :selectedWines="selectedWines"  />
						<Step4 v-if="step === 3" @backFromStep4="handleBackFromStep4" @okFromStep4="handleOkFromStep4" :selectedWinery="selectedWinery" :selectedWines="selectedWines" />
					</div>
				</div>
			</template>

		</b-modal>

		<RestoreDraftModal ref="restoreDraftModal" @confirmRestore="handleConfirmRestore" @cancelRestore="handleCancelRestore" />
	</div>
</template>

<script>
import StepNavigation from '@/components/modals/addWine/StepNavigation.vue'
import Step1 from '@/components/modals/addWine/step1/Step1.vue'
import Step2 from '@/components/modals/addWine/step2/Step2.vue'
import Step3 from '@/components/modals/addWine/step3/Step3.vue'
import Step4 from '@/components/modals/addWine/step4/Step4.vue';
import RestoreDraftModal from '@/components/modals/addWine/RestoreDraftModal.vue'
import DataService from '@/services/StrapiService'
import DataMappingService from '@/services/DataMappingService'
import { mapGetters } from 'vuex'
import Tracking from '@/utils/tracking'
import * as Utils from '@/utils/functions'


export default {
	name: 'CreateWinesModal',
	data() {
		return {
			modalId: 'create-wines-modal',
			step: 0,
			selectedWinery: {},
			selectedWines: [],
			pendingDraft: null,
			restoreInProgress: false,
			stepTitles: [this.$t('winelist.addWineModal.step1'), this.$t('winelist.addWineModal.step2'), this.$t('winelist.addWineModal.step3'), this.$t('winelist.addWineModal.step4')]
		}
	},
  mounted() {
    this.fetchGrapeVarieties()
  },
	components: {
		StepNavigation,
		Step1,
		Step2,
		Step3,
		Step4,
		RestoreDraftModal
	},
	methods: {
    fetchGrapeVarieties() {
      if (this.getGrapeVarieties.length === 0) {
        DataService.getAllCuratedGrapeVarieties().then((response) => {
          this.$store.commit('setGrapeVarieties', DataMappingService.mapGrapeVarietyArrayApiToGrapeVarietyArrayUi(response.data))
        }).catch((err) => {
          console.log(err)
        })
      }
    },
		handleOkFromStep1(winery) {
			this.selectedWinery = winery
			this.step = 1
		},
		handleOkFromStep2(selectedWines) {
			this.selectedWines = selectedWines
			this.step = 2
		},
		handleBackFromStep2() {
			this.resetModal()
		},
		handleOkFromStep3(selectedWines) {
			this.selectedWines = selectedWines
			this.step = 3
			console.log('[DRAFT] handleOkFromStep3 – saving draft', selectedWines.length, 'wines')
			this.saveDraft()
		},
		/**
		 * Handle the back event from step 3
		 * override the selected wines with the complemented wines to keep the user's
		 * inputs from step 3
		 */
		handleBackFromStep3(selectedWines) {
			this.selectedWines = selectedWines
			this.step = 1
		},
		handleOkFromStep4(venueWineIds) {
      Tracking.trackEvent(
        Tracking.CATEGORY_VENUE_WINE,
        Tracking.ACTION_CREATE_PRODUCT,
        venueWineIds.join(',')
      );
			this.clearDraft()
			// Clear data before hiding so onHidden won't re-save the draft
			this.selectedWinery = {}
			this.selectedWines = []
			this.step = 0
			this.hideModal()
			this.$emit('addedNewVenueWines', venueWineIds)
		},
		handleBackFromStep4() {
			this.step = 2
		},
		showModal() {
			this.$bvModal.show(this.modalId)
		},
		hideModal() {
			this.$bvModal.hide(this.modalId)
			this.step = 0
			this.resetModal()
		},
		/**
		 * Called when the modal is hidden (closed by X, Escape, or hideModal).
		 * Saves the current state as a draft before resetting.
		 */
		onHide() {
			console.log('[DRAFT] onHide – restoreInProgress:', this.restoreInProgress, '| step:', this.step, '| selectedWines:', this.selectedWines.length)
			// @hide fires when the modal is about to close, but also when a child modal
			// (RestoreDraftModal) opens on top. Skip saving during restore to avoid
			// overwriting the draft with stale data.
			if (this.restoreInProgress) {
				console.log('[DRAFT] onHide – blocked by restoreInProgress')
				return
			}
			// Pull latest data from the active step – the user may have made changes
			// in Step 2 or Step 3 without clicking "Weiter"
			if (this.step === 1 && this.$refs.step2) {
				this.selectedWines = this.$refs.step2.selectedWineList
			} else if (this.step === 2 && this.$refs.step3) {
				this.selectedWines = this.$refs.step3.selectedWineList
			}
			if (this.selectedWines.length > 0) {
				console.log('[DRAFT] onHide – saving draft with', this.selectedWines.length, 'wines')
				this.saveDraft()
			} else {
				console.log('[DRAFT] onHide – no wines to save')
			}
		},
		/**
		 * Called when the modal is fully visible.
		 * Checks for a saved draft and offers to restore it.
		 */
		onShown() {
			// Reset guard – may be stuck from a previous cycle
			this.restoreInProgress = false
			const draft = Utils.loadAddWineDraft()
			if (draft && draft.selectedWines && draft.selectedWines.length > 0) {
				this.pendingDraft = draft
				this.restoreInProgress = true
				this.$refs.restoreDraftModal.showModal()
			}
		},
		handleConfirmRestore() {
			if (this.pendingDraft) {
				this.selectedWinery = this.pendingDraft.selectedWinery
				this.selectedWines = this.pendingDraft.selectedWines
				this.step = this.pendingDraft.step
				this.pendingDraft = null
			}
			Utils.clearAddWineDraft()
			this.restoreInProgress = false
		},
		handleCancelRestore() {
			this.pendingDraft = null
			Utils.clearAddWineDraft()
			this.restoreInProgress = false
			// Reset to fresh start so the user sees Step 1 immediately
			this.selectedWinery = {}
			this.selectedWines = []
			this.step = 0
		},
		saveDraft() {
			Utils.saveAddWineDraft(this.selectedWinery, this.selectedWines, this.step)
		},
		clearDraft() {
			Utils.clearAddWineDraft()
		},
		resetModal() {
			this.selectedWinery = {}
			this.selectedWines = []
			this.step = 0
		},
		clickOutSide() {
      this.$store.commit('clickedOutside')
    },
	},
  computed: {
    ...mapGetters(['getGrapeVarieties'])
  }
}

</script>